import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Car, Plus, Wrench, Users, AlertTriangle, Trash2, UserPlus } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday } from '@/lib/dateRange';

export default function FleetPage() {
  const [tab, setTab] = useState('vehicles');
  const [vehicles, setVehicles] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [stats, setStats] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showMaint, setShowMaint] = useState(false);
  const [showAssign, setShowAssign] = useState(null);
  const [form, setForm] = useState({ plate_number: '', make: '', model: '', year: '', type: 'sedan', fuel_type: 'gasoline' });
  const [maintForm, setMaintForm] = useState({ vehicle_id: '', type: 'routine', description: '', cost: 0, date: localToday() });

  const fetchData = async () => {
    try {
      const [vRes, mRes, sRes, eRes] = await Promise.all([
        api.get('/fleet/vehicles'), api.get('/fleet/maintenance'), api.get('/fleet/stats'), api.get('/employees'),
      ]);
      setVehicles(vRes.data);
      setMaintenance(mRes.data);
      setStats(sRes.data);
      setEmployees(eRes.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!form.plate_number.trim()) { toast.error('Plate number required'); return; }
    try { await api.post('/fleet/vehicles', form); toast.success('Vehicle added'); setShowCreate(false); setForm({ plate_number: '', make: '', model: '', year: '', type: 'sedan', fuel_type: 'gasoline' }); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try { await api.delete(`/fleet/vehicles/${id}`); toast.success('Deleted'); fetchData(); } catch { toast.error('Failed'); }
  };

  const handleAssign = async (vehicleId, empId) => {
    try { await api.post(`/fleet/vehicles/${vehicleId}/assign`, { employee_id: empId || null }); toast.success(empId ? 'Assigned' : 'Unassigned'); setShowAssign(null); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleMaintenance = async () => {
    if (!maintForm.vehicle_id) { toast.error('Select a vehicle'); return; }
    try { await api.post('/fleet/maintenance', maintForm); toast.success('Maintenance logged'); setShowMaint(false); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const statusColors = { available: 'bg-success/20 text-success', assigned: 'bg-info/20 text-info', maintenance: 'bg-warning/20 text-warning', retired: 'bg-muted text-muted-foreground' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fleet-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-lg shadow-indigo-500/20">
              <Car size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight dark:text-white">Fleet Management</h1>
              <p className="text-sm text-muted-foreground">{stats.total_vehicles || 0} vehicles</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowMaint(true)} variant="outline" data-testid="log-maintenance-btn"><Wrench size={14} className="mr-1.5" />Log Maintenance</Button>
            <Button onClick={() => setShowCreate(true)} data-testid="add-vehicle-btn"><Plus size={14} className="mr-1.5" />Add Vehicle</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Total</div><div className="text-xl font-bold dark:text-white" data-testid="fleet-stat-total">{stats.total_vehicles || 0}</div></CardContent></Card>
          <Card className="border-l-4 border-l-emerald-500"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Available</div><div className="text-xl font-bold text-success">{stats.available || 0}</div></CardContent></Card>
          <Card className="border-l-4 border-l-blue-500"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Assigned</div><div className="text-xl font-bold text-info">{stats.assigned || 0}</div></CardContent></Card>
          <Card className="border-l-4 border-l-amber-500"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Maint. Due</div><div className="text-xl font-bold text-warning">{stats.maintenance_due || 0}</div></CardContent></Card>
          <Card className="border-l-4 border-l-red-500"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Ins. Expiring</div><div className="text-xl font-bold text-error">{stats.insurance_expiring || 0}</div></CardContent></Card>
          <Card><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Maint. Cost</div><div className="text-xl font-bold dark:text-white">{stats.total_maintenance_cost?.toLocaleString() || 0}</div></CardContent></Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="vehicles" data-testid="tab-vehicles"><Car size={14} className="mr-1" />Vehicles</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench size={14} className="mr-1" />Maintenance Log</TabsTrigger>
          </TabsList>

          <TabsContent value="vehicles">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {vehicles.length === 0 ? <div className="col-span-full text-center py-12 text-muted-foreground">No vehicles yet.</div> :
               vehicles.map(v => (
                <Card key={v.id} data-testid={`vehicle-card-${v.id}`}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-bold dark:text-white">{v.plate_number}</p>
                        <p className="text-xs text-muted-foreground">{v.make} {v.model} {v.year}</p>
                      </div>
                      <Badge className={`text-[10px] ${statusColors[v.status] || ''}`}>{v.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-[10px] text-muted-foreground mb-2">
                      <span>Type: {v.type}</span><span>Fuel: {v.fuel_type}</span>
                      <span>Mileage: {v.mileage?.toLocaleString() || 0} km</span>
                      {v.assigned_employee_name && <span className="text-info">Assigned: {v.assigned_employee_name}</span>}
                    </div>
                    {v.next_maintenance && v.next_maintenance <= localToday() && (
                      <div className="text-[10px] text-warning flex items-center gap-1 mb-2"><AlertTriangle size={10} />Maintenance overdue</div>
                    )}
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowAssign(v)} data-testid={`assign-btn-${v.id}`}><UserPlus size={12} className="mr-1" />{v.assigned_to ? 'Reassign' : 'Assign'}</Button>
                      <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => handleDelete(v.id)}><Trash2 size={12} /></Button>
                    </div>
                  </CardContent>
                </Card>
               ))}
            </div>
          </TabsContent>

          <TabsContent value="maintenance">
            <Card>
              <CardContent className="pt-4">
                {maintenance.length === 0 ? <p className="text-center py-8 text-muted-foreground text-sm">No maintenance records.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="maintenance-table">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="pb-2 pr-3">Date</th><th className="pb-2 pr-3">Vehicle</th><th className="pb-2 pr-3">Type</th><th className="pb-2 pr-3">Description</th><th className="pb-2 pr-3">Cost</th><th className="pb-2">Mileage</th>
                      </tr></thead>
                      <tbody>
                        {maintenance.map(m => {
                          const v = vehicles.find(x => x.id === m.vehicle_id);
                          return (
                            <tr key={m.id} className="border-b last:border-0">
                              <td className="py-2 pr-3">{m.date}</td>
                              <td className="py-2 pr-3 font-medium dark:text-white">{v?.plate_number || m.vehicle_id}</td>
                              <td className="py-2 pr-3"><Badge variant="outline" className="text-[10px]">{m.type}</Badge></td>
                              <td className="py-2 pr-3 text-xs">{m.description || '-'}</td>
                              <td className="py-2 pr-3">{m.cost?.toLocaleString()}</td>
                              <td className="py-2">{m.mileage?.toLocaleString() || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Vehicle */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent data-testid="add-vehicle-dialog">
            <DialogHeader><DialogTitle>Add Vehicle</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Plate Number *</Label><Input value={form.plate_number} onChange={(e) => setForm(f => ({ ...f, plate_number: e.target.value }))} data-testid="vehicle-plate-input" className="mt-1" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label>Make</Label><Input value={form.make} onChange={(e) => setForm(f => ({ ...f, make: e.target.value }))} className="mt-1" /></div>
                <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm(f => ({ ...f, model: e.target.value }))} className="mt-1" /></div>
                <div><Label>Year</Label><Input value={form.year} onChange={(e) => setForm(f => ({ ...f, year: e.target.value }))} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label><Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="sedan">Sedan</SelectItem><SelectItem value="suv">SUV</SelectItem><SelectItem value="truck">Truck</SelectItem><SelectItem value="van">Van</SelectItem><SelectItem value="bus">Bus</SelectItem></SelectContent></Select></div>
                <div><Label>Fuel</Label><Select value={form.fuel_type} onValueChange={(v) => setForm(f => ({ ...f, fuel_type: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="gasoline">Gasoline</SelectItem><SelectItem value="diesel">Diesel</SelectItem><SelectItem value="electric">Electric</SelectItem><SelectItem value="hybrid">Hybrid</SelectItem></SelectContent></Select></div>
              </div>
              <Button onClick={handleCreate} className="w-full" data-testid="submit-vehicle-btn">Add Vehicle</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Assign Vehicle */}
        <Dialog open={!!showAssign} onOpenChange={() => setShowAssign(null)}>
          <DialogContent data-testid="assign-vehicle-dialog">
            <DialogHeader><DialogTitle>Assign Vehicle: {showAssign?.plate_number}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Select onValueChange={(v) => handleAssign(showAssign?.id, v === '__unassign__' ? null : v)}>
                <SelectTrigger data-testid="assign-emp-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassign__">Unassign Vehicle</SelectItem>
                  {employees.filter(e => e.status === 'active').map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </DialogContent>
        </Dialog>

        {/* Log Maintenance */}
        <Dialog open={showMaint} onOpenChange={setShowMaint}>
          <DialogContent data-testid="log-maintenance-dialog">
            <DialogHeader><DialogTitle>Log Maintenance</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Vehicle *</Label><Select value={maintForm.vehicle_id || 'none'} onValueChange={(v) => setMaintForm(f => ({ ...f, vehicle_id: v === 'none' ? '' : v }))}><SelectTrigger className="mt-1" data-testid="maint-vehicle-select"><SelectValue placeholder="Select vehicle" /></SelectTrigger><SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate_number} - {v.make} {v.model}</SelectItem>)}</SelectContent></Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label><Select value={maintForm.type} onValueChange={(v) => setMaintForm(f => ({ ...f, type: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="routine">Routine</SelectItem><SelectItem value="repair">Repair</SelectItem><SelectItem value="inspection">Inspection</SelectItem><SelectItem value="accident">Accident</SelectItem></SelectContent></Select></div>
                <div><Label>Date</Label><Input type="date" value={maintForm.date} onChange={(e) => setMaintForm(f => ({ ...f, date: e.target.value }))} className="mt-1" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={maintForm.description} onChange={(e) => setMaintForm(f => ({ ...f, description: e.target.value }))} rows={2} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Cost</Label><Input type="number" value={maintForm.cost} onChange={(e) => setMaintForm(f => ({ ...f, cost: parseFloat(e.target.value) || 0 }))} className="mt-1" /></div>
                <div><Label>Mileage</Label><Input type="number" value={maintForm.mileage || ''} onChange={(e) => setMaintForm(f => ({ ...f, mileage: parseInt(e.target.value) || 0 }))} className="mt-1" /></div>
              </div>
              <Button onClick={handleMaintenance} className="w-full" data-testid="submit-maintenance-btn">Log Maintenance</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
